import React from 'react';

/**
 * Section 5 : État de santé et autonomie
 */
const AssessmentSection5_Sante = ({ data, onChange }) => {
  const handleChange = (field, value) => {
    onChange('health', {
      ...data,
      [field]: value
    });
  };

  const handleNestedChange = (category, field, value) => {
    onChange('health', {
      ...data,
      [category]: {
        ...data[category],
        [field]: value
      }
    });
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = data?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    handleChange(field, newArray);
  };

  return (
    <div className="assessment-section-content">
      <h3 className="assessment-section-title">ÉTAT DE SANTÉ ET AUTONOMIE</h3>

      <div className="assessment-form-group">
        <label>Comment évaluez-vous votre état de santé général ?</label>
        <select
          className="assessment-form-control"
          value={data?.generalHealth || ''}
          onChange={(e) => handleChange('generalHealth', e.target.value)}
        >
          <option value="">-- Sélectionner --</option>
          <option value="tres-bon">Très bon</option>
          <option value="bon">Bon</option>
          <option value="moyen">Moyen</option>
          <option value="mauvais">Mauvais</option>
          <option value="tres-mauvais">Très mauvais</option>
        </select>
      </div>

      <div className="assessment-form-group">
        <label>Principales pathologies ou problèmes de santé</label>
        <textarea
          className="assessment-form-control"
          rows="4"
          placeholder="Décrivez les principales pathologies..."
          value={data?.mainPathologies || ''}
          onChange={(e) => handleChange('mainPathologies', e.target.value)}
        />
      </div>

      <div className="assessment-form-group">
        <label>Date du dernier bilan médical</label>
        <input
          type="date"
          className="assessment-form-control"
          value={data?.lastCheckup || ''}
          onChange={(e) => handleChange('lastCheckup', e.target.value)}
        />
      </div>

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Affections courantes
      </h4>

      <div className="assessment-form-group">
        <label>Souffrez-vous de (cochez toutes les cases applicables) :</label>
        <div className="assessment-checkbox-group">
          {['Maladie cardiovasculaire', 'Diabète', 'Problèmes respiratoires', 'Troubles cognitifs/Mémoire', 'Maladie de Parkinson', 'Troubles psychiatriques', 'Cancer', 'Problèmes de vue', 'Problèmes d\'audition', 'Douleurs chroniques', 'Incontinence'].map(condition => (
            <label key={condition} className="assessment-checkbox-label">
              <input
                type="checkbox"
                checked={data?.conditions?.includes(condition) || false}
                onChange={() => handleArrayToggle('conditions', condition)}
              />
              <span>{condition}</span>
            </label>
          ))}
        </div>
      </div>

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Médicaments
      </h4>

      <div className="assessment-form-group">
        <label>Prenez-vous des médicaments régulièrement ?</label>
        <div className="assessment-radio-group">
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="takesRegularly"
              checked={data?.medications?.takesRegularly === false}
              onChange={() => handleNestedChange('medications', 'takesRegularly', false)}
            />
            <span>Non</span>
          </label>
          <label className="assessment-radio-label">
            <input
              type="radio"
              name="takesRegularly"
              checked={data?.medications?.takesRegularly === true}
              onChange={() => handleNestedChange('medications', 'takesRegularly', true)}
            />
            <span>Oui</span>
          </label>
        </div>
      </div>

      {data?.medications?.takesRegularly && (
        <>
          <div className="assessment-grid-2">
            <div className="assessment-form-group">
              <label>Nombre de médicaments par jour</label>
              <input
                type="number"
                min="0"
                className="assessment-form-control"
                value={data?.medications?.count || ''}
                onChange={(e) => handleNestedChange('medications', 'count', parseInt(e.target.value) || null)}
              />
            </div>

            <div className="assessment-form-group">
              <label>Avez-vous besoin d'aide pour la prise ?</label>
              <div className="assessment-radio-group">
                <label className="assessment-radio-label">
                  <input
                    type="radio"
                    name="needsHelp"
                    checked={data?.medications?.needsHelp === false}
                    onChange={() => handleNestedChange('medications', 'needsHelp', false)}
                  />
                  <span>Non</span>
                </label>
                <label className="assessment-radio-label">
                  <input
                    type="radio"
                    name="needsHelp"
                    checked={data?.medications?.needsHelp === true}
                    onChange={() => handleNestedChange('medications', 'needsHelp', true)}
                  />
                  <span>Oui</span>
                </label>
              </div>
            </div>
          </div>

          <div className="assessment-form-group">
            <label>Avez-vous un pilulier préparé ?</label>
            <div className="assessment-radio-group">
              <label className="assessment-radio-label">
                <input
                  type="radio"
                  name="hasPillBox"
                  checked={data?.medications?.hasPillBox === false}
                  onChange={() => handleNestedChange('medications', 'hasPillBox', false)}
                />
                <span>Non</span>
              </label>
              <label className="assessment-radio-label">
                <input
                  type="radio"
                  name="hasPillBox"
                  checked={data?.medications?.hasPillBox === true}
                  onChange={() => handleNestedChange('medications', 'hasPillBox', true)}
                />
                <span>Oui</span>
              </label>
            </div>
          </div>

          {data?.medications?.hasPillBox && (
            <div className="assessment-form-group">
              <label>Préparé par</label>
              <input
                type="text"
                className="assessment-form-control"
                placeholder="Moi-même, infirmier(ère), proche..."
                value={data?.medications?.preparedBy || ''}
                onChange={(e) => handleNestedChange('medications', 'preparedBy', e.target.value)}
              />
            </div>
          )}
        </>
      )}

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Professionnels de santé
      </h4>

      <div className="assessment-grid-2">
        <div className="assessment-form-group">
          <label>Médecin traitant - Nom</label>
          <input
            type="text"
            className="assessment-form-control"
            value={data?.professionals?.doctor?.name || ''}
            onChange={(e) => handleNestedChange('professionals', 'doctor', {
              ...data?.professionals?.doctor,
              name: e.target.value
            })}
          />
        </div>

        <div className="assessment-form-group">
          <label>Téléphone</label>
          <input
            type="tel"
            className="assessment-form-control"
            value={data?.professionals?.doctor?.phone || ''}
            onChange={(e) => handleNestedChange('professionals', 'doctor', {
              ...data?.professionals?.doctor,
              phone: e.target.value
            })}
          />
        </div>
      </div>

      <h4 style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '24px', marginBottom: '16px' }}>
        Mobilité
      </h4>

      <div className="assessment-form-group">
        <label>Comment vous déplacez-vous dans votre logement ?</label>
        <select
          className="assessment-form-control"
          value={data?.mobility?.indoor || ''}
          onChange={(e) => handleNestedChange('mobility', 'indoor', e.target.value)}
        >
          <option value="">-- Sélectionner --</option>
          <option value="autonome">Sans aide, de manière autonome</option>
          <option value="canne">Avec une canne</option>
          <option value="deambulateur">Avec un déambulateur</option>
          <option value="fauteuil-manuel">En fauteuil roulant manuel</option>
          <option value="fauteuil-electrique">En fauteuil roulant électrique</option>
          <option value="avec-aide">Avec l'aide d'une personne</option>
          <option value="alite">Alité(e) en permanence</option>
        </select>
      </div>

      <div className="assessment-form-group">
        <label>Risque de chute</label>
        <select
          className="assessment-form-control"
          value={data?.mobility?.fallRisk || ''}
          onChange={(e) => handleNestedChange('mobility', 'fallRisk', e.target.value)}
        >
          <option value="">-- Sélectionner --</option>
          <option value="aucun">Aucun risque identifié</option>
          <option value="faible">Risque faible</option>
          <option value="modere">Risque modéré</option>
          <option value="eleve">Risque élevé</option>
        </select>
      </div>

      <div className="assessment-form-group">
        <label>Nombre de chutes au cours des 6 derniers mois</label>
        <input
          type="number"
          min="0"
          className="assessment-form-control"
          value={data?.mobility?.fallsLast6Months || ''}
          onChange={(e) => handleNestedChange('mobility', 'fallsLast6Months', parseInt(e.target.value) || null)}
        />
      </div>

      <div className="assessment-form-group">
        <label>Pouvez-vous sortir de votre domicile seul(e) ?</label>
        <select
          className="assessment-form-control"
          value={data?.mobility?.canGoOutAlone || ''}
          onChange={(e) => handleNestedChange('mobility', 'canGoOutAlone', e.target.value)}
        >
          <option value="">-- Sélectionner --</option>
          <option value="oui">Oui, sans difficulté</option>
          <option value="difficulte">Oui, mais avec difficulté</option>
          <option value="accompagnement">Non, j'ai besoin d'accompagnement</option>
          <option value="non">Je ne sors plus</option>
        </select>
      </div>
    </div>
  );
};

export default AssessmentSection5_Sante;
